import { and, eq, inArray } from "drizzle-orm";
import { select } from "gamedata/GameData";
import { redirect } from "react-router";
import { z } from "zod";
import { PageEditor } from "~/components/site/PageEditor";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { tt, tturl, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { loadMarkdown } from "~/util/Markdown.server";
import { getImagesForPage } from "~/util/page";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

$PAGE({
  path: "npcs/:npcId/edit-page",

  loader: $LOADER?.(async (cx) => {
    // Log
    logPageView(cx);
    //
    const Params = z.object({ npcId: z.string() });
    const params = Params.parse(cx.params);
    const t = tt.bind(null, cx.realm.lang);

    const gd_npc = cx.gd.npcs.get(params.npcId);
    if (gd_npc == null) {
      throw Response.json(
        { msg: t({ en: "NPC not found.", ru: "НПЦ не найден." }) },
        { status: 404 }
      );
    }

    const page = await pg.db
      .select({
        id: pg.mdPages.id,
        game: pg.mdPages.game,
        revision: {
          id: pg.mdRevisions.id,
          titleEn: pg.mdRevisions.titleEn,
          contentEn: pg.mdRevisions.contentEn,
          titleRu: pg.mdRevisions.titleRu,
          contentRu: pg.mdRevisions.contentRu,
          createdAt: pg.mdRevisions.createdAt,
          images: pg.mdRevisions.images,
        },
      })
      .from(pg.mdPages)
      .where(eq(pg.mdPages.npcName, params.npcId))
      .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))
      .then((res) => res.at(0));

    if (!page) {
      return redirect(tturl(cx.realm, `/npcs/${gd_npc.npcName}`));
    }

    const images = await getImagesForPage(page.id, page.revision.images ?? []);

    const npc = select(gd_npc, {
      npcName: true,
      name: { en: true, ru: true },
      nick: { en: true, ru: true },
      level: true,
    });

    return {
      npc,
      page: {
        ...page,
        images,
        content: {
          en: await loadMarkdown(
            page.revision.contentEn ?? "",
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          ),
          ru: await loadMarkdown(
            page.revision.contentRu ?? "",
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          ),
        },
      },
      authUser: cx.req.sess?.user,
    };
  }),

  meta: (args) => {
    const { npc } = args.data;
    const pageTitle = args.t({ en: "Page Editor", ru: "Редактор страницы" });
    let nameRu = npc.name.ru;
    let nameEn = npc.name.en;
    const level = npc.level;
    if (npc.nick.en !== "") nameEn = `${npc.nick.en} ${npc.name.en}`;
    if (npc.nick.ru !== "") nameEn = `${npc.nick.en} ${npc.name.en}`;

    const name = args.t({
      en: `${nameEn} Lvl. ${level}`,
      ru: `${nameRu} (${nameEn}) Ур. ${level}`,
    });
    const title = `${pageTitle} - ${name} - ${args.t({
      en: "NPCs",
      ru: "НПЦ",
    })} - ${homePage(args.realm)}`;
    const description = args.t({
      en: `Wiki Page Editor ${name}`,
      ru: `Редактор Wiki-Страницы ${name}`,
    });
    return {
      title,
      description,
      canonicalUrl: `/npcs/${npc.npcName}/edit-page`,
    };
  },

  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const { npc } = data;
    const pageTitle = t({ en: "Page Editor", ru: "Редактор страницы" });
    const nick = t({ en: data.npc.nick.en, ru: data.npc.nick.ru });

    let fullName = t({ en: npc.name.en, ru: npc.name.ru });
    if (nick !== "") {
      fullName = t({
        en: `${npc.nick.en} ${npc.name.en}`,
        ru: `${npc.nick.ru} ${npc.name.ru}`,
      });
    }
    const npcsTitle = t({ en: "NPCs", ru: "НПЦ" });
    const breadcrumbList = [
      { title: npcsTitle, url: turl("/npcs") },
      { title: fullName, url: turl(`/npcs/${data.npc.npcName}`) },
      {
        title: pageTitle,
        url: turl(`/npcs/${npc.npcName}/edit-page`),
      },
    ];
    return (
      <PageEditor
        authUser={data.authUser}
        target={{ _: "npc", npcName: npc.npcName }}
        page={data.page}
        breadcrumbList={breadcrumbList}
      />
    );
  },
});
